import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { Button } from '~/shared/components/Button';
import { CheckBox, Input } from '~/shared/components/Form';
import { Text } from '~/shared/components/Text';
import { InputSection, Paragraph, StyledButton, StyledForm, StyledText } from './styled';
import useAddUser from '~/services/shop/hooks/mit-fog/useAddUser';
import { Validation } from '~/shared/utils/validation';
import CheckmarkIcon from '~/icons/iconography/checkmark.svg';

type Props = {
    handleClose: () => void;
    user: ShopTypes.CustomerUserItemDto | undefined;
    customerId?: string;
};

export default function AddUsers({ handleClose, user, customerId }: Props) {
    const { translate } = useTranslations();

    const methods = useForm<FieldValues>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
    });

    const { mutate: addUser, isLoading } = useAddUser();

    const onSubmitHandler: SubmitHandler<FieldValues> = (data) => {
        const {
            administrator,
            email,
            phoneNumber,
            priceExportAllowed,
            seeAccountInformationRole,
            seePricesRole,
            userName,
        } = data;

        if (!customerId) {
            return;
        }

        let rolesSet = new Set(user?.roles || []);
        if (administrator) rolesSet.add(1);
        if (seeAccountInformationRole) rolesSet.add(2);
        if (priceExportAllowed) rolesSet.add(5);
        if (seePricesRole) rolesSet.add(7);

        const updatedData: ShopTypes.UserDto = {
            email: email,
            customerId: customerId,
            userName: userName,
            phoneNumber: phoneNumber,
            roles: Array.from(rolesSet),
        };

        addUser(
            {
                users: updatedData,
            },
            {
                onSuccess: () => {
                    handleClose();
                    methods.reset();
                },
            },
        );
    };
    const getRequiredText = (field: string) =>
        `${field} ${translate('checkout.requiredField.text')}`;

    return (
        <>
            <Text variant="display2">{translate('mit-fog.userList.createUser')}</Text>
            <StyledForm methods={methods} onSubmit={onSubmitHandler}>
                <InputSection>
                    <Input
                        name="email"
                        placeholder="E-mail"
                        required={getRequiredText('E-mail')}
                        pattern={{
                            value: Validation.Email,
                            message: translate('checkout.validation.email'),
                        }}
                        type="email"
                    />
                    <Input
                        name="userName"
                        placeholder="Navn"
                        required={getRequiredText('Fornavn')}
                        type="text"
                    />
                    <Input
                        name="phoneNumber"
                        hideStar
                        pattern={{
                            value: Validation.PhoneNumber,
                            message: translate('checkout.validation.phoneNumber'),
                        }}
                        placeholder="Telefonnr."
                        type="tel"
                    />
                </InputSection>
                <div>
                    <StyledText variant="display5">
                        {translate('mit-fog.userList.adminRights')}
                    </StyledText>
                    <CheckBox name="seeAccountInformationRole">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.viewAccountInfo')}
                        </Paragraph>
                    </CheckBox>
                    <CheckBox name="administrator">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.manageUserRights')}
                        </Paragraph>
                    </CheckBox>
                    <CheckBox name="priceExportAllowed">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.priceExportAllowed')}
                        </Paragraph>
                    </CheckBox>
                    <CheckBox name="seePricesRole">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.viewPricesInfo')}
                        </Paragraph>
                    </CheckBox>
                </div>
                <Button
                    type="submit"
                    fullWidth
                    disabled={isLoading || !methods.formState.isValid}
                    customIcon={<CheckmarkIcon />}
                >
                    {translate('mit-fog.userProfile.userInformation.saveChanges')}
                </Button>
            </StyledForm>
            <StyledButton onClick={handleClose} fullWidth variant="stroke" noIcon>
                {translate('mit-fog.userProfile.userInformation.cancel')}
            </StyledButton>
        </>
    );
}
